import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import ButtonBase from "@material-ui/core/ButtonBase";
import Checkbox from "@material-ui/core/Checkbox";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography/Typography";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";

import { withStyles } from "@material-ui/core/styles";

import DownIcon from "mdi-material-ui/ChevronDown";
import OpenInNewIcon from "mdi-material-ui/OpenInNew";
import UpIcon from "mdi-material-ui/ChevronUp";

import Button from "components/Button/Button";
import Loading from "components/Loading/Loading";
import TermsDialog from "components/TermsDialog/TermsDialog";
import TextInput from "components/TextInput/TextInput";
import { Autocomplete } from "@material-ui/lab";
import { Grid, MenuItem } from "@material-ui/core";

const styles = (theme) => ({
  textField: {
    height: 45,
  },
  box: {
    width: "100%",
    //marginTop: 20,
    //paddingBottom: 20,
    textAlign: "center",
  },
  form: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  button: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  employerButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
  },
  headline: {
    marginBottom: theme.spacing(2),
    textAlign: "center",
  },
  loading: {
    marginLeft: theme.spacing(1),
  },
  error: {
    minHeight: theme.spacing(2),
    color: "red",
    textAlign: "center",
  },
  text: {
    minHeight: theme.spacing(2),
    textAlign: "center",
  },
  termsContainer: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(3),
    width: "100%",
  },
  termsButton: {
    background: "rgba(20, 123, 164, 0.5)",
    marginBottom: theme.spacing(0.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(1.5),
    transition: theme.transitions.create(["background"]),
    "&:hover": {
      background: "rgba(20, 123, 164, 0.75)",
    },
  },
  termsButtonIcon: {
    fontSize: "inherit",
    marginLeft: theme.spacing(1),
  },
  hr: {
    borderColor: "rgba(255,255,255,0.5)",
    borderTop: "none",
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    width: "100%",
  },
  dateContainer: {
    display: "flex",
    width: "100%",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.25),
    marginTop: theme.spacing(0.25),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  leftAlign: {
    '& .MuiInputBase-input, & .MuiSelect-select': {
      textAlign: 'left',
    },
  },
});

function RegistrationForm(props) {
  const { t } = useTranslation("common");
  const {
    classes,
    data,
    passwordHelper,
    password2Helper,
    error,
    loading,
    register,
    submitEnabled,
    foundByBusinessId,
    businessIdCountry,
    validCompany,
    companyData,
    taxNumberLoading,
  } = props;

  const companyInfoOk = data.name.value === "" || data.business_id.value === "";
  const [eBtn, eBtnSet] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const showCompany = eBtn || false;
  const sender = data.sender ? data.sender.value : null;
  const taxBlurGuardRef = React.useRef({ value: null, ts: 0 });

  // Prevent duplicate tax number blur handling
  const handleGuardTaxNumberBlur = (e) => {
    const val = e.target.value;
    const now = Date.now();
    const sig = val;

    if (
      taxBlurGuardRef.current.value === sig &&
      now - taxBlurGuardRef.current.ts < 1000
    ) {
      console.warn('Skipping duplicate taxNumber blur:', sig);
      return;
    }
    taxBlurGuardRef.current = { value: sig, ts: now };

    if (props.onBlurTaxNumber) {
      props.onBlurTaxNumber(e);
    }
  };

  const employmentRelationshipOptions = t(
    "registrations.employment_relationship_options",
    {
      returnObjects: true,
    }
  );

  useEffect(() => {
    if (!foundByBusinessId) {
      eBtnSet(false);
    }
  }, [foundByBusinessId]);

  /**
   * Login when pressing enter.
   * @param {*} event
   */
  function handleKeyPress(event) {
    if (event.charCode === 13 && submitEnabled) {
      event.preventDefault();
      event.stopPropagation();
      register();
    }
  }

  // const renderInputs = (prefill) => (input) => {
  //   const RenderInput = (
  //     <TextInput
  //       fullWidth={true}
  //       key={input[0]}
  //       label={input[1]}
  //       margin="dense"
  //       onChange={props.onChangeData(input[0])}
  //       readOnly={prefill}
  //       value={data[input[0]].value}
  //     />
  //   )
  //   return RenderInput;
  // }

  // amazonq-ignore-next-line
  return (
    <form noValidate autoComplete="off" className={classes.form}>
      <Typography variant="h5" className={classes.headline}>
        {t("auth.register_user")}
        {sender && (
          <div>
            <br />
            Kutsuja: {sender.first_name} {sender.last_name}
            <br />
            {sender.phone}
            <br />
            {sender.email}
          </div>
        )}
      </Typography>

      <Typography variant="body1" className={classes.error}>
        {error ? error : ""}
      </Typography>

      <TextInput
        error={data.phone.error}
        fullWidth
        label={t("auth.phone_number")}
        margin="dense"
        name="username"
        onChange={(v, c, e, fv) => props.onChangeData("phone")(fv)}
        onBlur={(e) => props.onBlurPhonenumber(data.phone.value)}
        onKeyPress={handleKeyPress}
        disabled={true}
        required={data.phone.required}
        type="phone"
        value={data.phone.value}
      />
      <Typography variant="body2" className={classes.text}>
        {t("auth.date_of_birth")}
      </Typography>
      <div className={classes.dateContainer}>
        <TextInput
          error={data.dateOfBirthDay.error}
          label={t("auth.day")}
          fullWidth={true}
          margin="dense"
          name={"dateOfBirthDay"}
          onChange={props.onChangeData("dateOfBirthDay")}
          required={data.dateOfBirthDay.required}
          select
          value={data.dateOfBirthDay.value}
        >
          {Array.from({ length: data.dateOfBirthDay.max }, (_, i) => i + 1).map(
            (item) => (
              <MenuItem key={item} value={item} disabled={loading}>
                {item}
              </MenuItem>
            )
          )}
        </TextInput>
        <TextInput
          error={data.dateOfBirthMonth.error}
          label={t("auth.month")}
          fullWidth={true}
          margin="dense"
          max={12}
          min={1}
          name={"dateOfBirthMonth"}
          onChange={props.onChangeData("dateOfBirthMonth")}
          required={data.dateOfBirthMonth.required}
          select
          value={data.dateOfBirthMonth.value}
        >
          {Array.from(
            { length: data.dateOfBirthMonth.max },
            (_, i) => i + 1
          ).map((item) => (
            <MenuItem key={item} value={item} disabled={loading}>
              {t(`months.` + (item - 1))}
            </MenuItem>
          ))}
        </TextInput>

        {/* Year Autocomplete */}
        <Autocomplete
          id="year-select"
          style={{ width: "100%" }}
          freeSolo
          onKeyPress={(e) => {
            if (!/[0-9]/.test(e.key)) {
              e.preventDefault();
            }
            handleKeyPress(e);
          }}
          inputProps={{
            maxLength: 4,
            pattern: "[0-9]*",
            inputMode: "numeric",
          }}
          options={Array.from(
            { length: data.dateOfBirthYear.max - data.dateOfBirthYear.min },
            (_, i) => (data.dateOfBirthYear.max - i).toString()
          )}
          value={data.dateOfBirthYear.value.toString()}
          onChange={(event, newValue) => {
            const syntheticEvent = { target: { value: newValue || "" } };
            props.onChangeData("dateOfBirthYear")(syntheticEvent);
            props.handleDateOfBirthYearOnBlur("dateOfBirthYear")(
              syntheticEvent
            );
          }}
          onInputChange={(event, newInputValue) => {
            const syntheticEvent = { target: { value: newInputValue || "" } };
            props.onChangeData("dateOfBirthYear")(syntheticEvent);
          }}
          renderInput={(params) => (
            <TextInput
              {...params}
              fullWidth={true}
              margin="dense"
              label={t("auth.year")}
              error={data.dateOfBirthYear.error}
              required={data.dateOfBirthYear.required}
              onBlur={(e) => {
                if (props.handleDateOfBirthYearOnBlur) {
                  props.handleDateOfBirthYearOnBlur("dateOfBirthYear")(e);
                }
              }}
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
        />
      </div>
      {data.dateOfBirth.error && (
        <Typography variant="caption" className={classes.error}>
          {t("auth.date_of_Birth_invalid")}
        </Typography>
      )}

      <TextInput
        error={data.taxNumber.error}
        fullWidth={true}
        inputProps={{
          maxLength: "12",
        }}
        label={t("auth.tax_number")}
        margin="dense"
        onBlur={(e) => {
          handleGuardTaxNumberBlur(e);
          props.onBlurInput();
        }}
        onChange={props.onChangeData("taxNumber")}
        onKeyPress={handleKeyPress}
        required={data.taxNumber.required}
        value={data.taxNumber.value}
      />
      {data.taxNumber.error && (
        <Typography variant="caption" className={classes.error}>
          {data.taxNumber.errorMessage
            ? data.taxNumber.errorMessage
            : t("auth.tax_number_invalid")}
        </Typography>
      )}
      <TextInput
        value={data.first_name.value}
        onChange={props.onChangeData("first_name")}
        onBlur={(e) => props.onBlurInput()}
        required={data.first_name.required}
        fullWidth={true}
        error={data.first_name.error}
        margin="dense"
        onKeyPress={handleKeyPress}
        label={t("auth.first_name")}
        disabled={taxNumberLoading}
      />

      <TextInput
        value={data.last_name.value}
        onChange={props.onChangeData("last_name")}
        onBlur={(e) => props.onBlurInput()}
        required={data.last_name.required}
        fullWidth={true}
        error={data.last_name.error}
        margin="dense"
        onKeyPress={handleKeyPress}
        label={t("auth.last_name")}
        disabled={taxNumberLoading}
      />

      <hr className={classes.hr} />

      <div className={classes.box}>
        <FormControl component="fieldset">
          <FormLabel component="legend">{t("auth.company_country")}</FormLabel>
          <RadioGroup
            row
            ari-label="businessIdCountry"
            name="businessIdCountry"
            defaultValue={"finland"}
            value={businessIdCountry}
            onChange={(e) => props.handleBusinessIdCountryRadioChange(e)}
          >
            <FormControlLabel
              value="finland"
              control={<Radio color="primary" />}
              label={t("auth.finland")}
              labelPlacement="end"
            />
            <FormControlLabel
              value="other"
              control={<Radio color="primary" />}
              label={t("auth.other")}
              labelPlacement="end"
            />
          </RadioGroup>
        </FormControl>
        <TextInput
          fullWidth={true}
          error={data.business_id.error}
          label={t("auth.business_id")}
          margin="dense"
          onChange={props.onChangeBusinessId}
          onBlur={(e) => props.onBlurInput()}
          onKeyPress={handleKeyPress}
          readOnly={false}
          required={data.business_id.required}
          value={data.business_id.value}
        />
        {data.business_id.error && (
          <Typography variant="caption" className={classes.error}>
            {data.business_id.errorMessage ? data.business_id.errorMessage : ""}
          </Typography>
        )}
        <TextInput
          error={data.name.error}
          fullWidth={true}
          label={t("auth.company_name")}
          margin="dense"
          onChange={props.onChangeData("name")}
          onBlur={(e) => props.onBlurInput()}
          onKeyPress={handleKeyPress}
          readOnly={data.prefill.value}
          required={data.name.required}
          value={data.name.value}
          disabled={validCompany}
        />
        <TextInput
          value={data.projectKey.value}
          disabled={data.projectKey.disabled}
          onChange={props.onChangeData("projectKey")}
          required={data.projectKey.required}
          onBlur={(e) => props.onBlurInput()}
          fullWidth={true}
          error={data.projectKey.error}
          margin="dense"
          onKeyPress={handleKeyPress}
          label={t("auth.project_key")}
        />
        <TextInput
          value={data.employmentRelationship.value}
          onChange={props.onChangeData("employmentRelationship")}
          onBlur={(e) => props.onBlurInput()}
          required={data.employmentRelationship.required}
          fullWidth={true}
          error={data.employmentRelationship.error}
          margin="dense"
          onKeyPress={handleKeyPress}
          label={t("auth.employment_relationship")}
          select
          disabled={taxNumberLoading}
          className={classes.leftAlign}
          inputProps={{ style: { textAlign: 'left' } }}
        >
          {Object.keys(employmentRelationshipOptions).map((key) => (
            <MenuItem key={key} value={key} disabled={loading}>
              {employmentRelationshipOptions[key]}
            </MenuItem>
          ))}
        </TextInput>
        {foundByBusinessId ? (
          <div className={classes.buttonContainer}>
            <Button
              className={classes.employerButton}
              variant="outlined"
              color="primary"
              onClick={() => eBtnSet(!eBtn)}
            >
              {showCompany ? t("auth.hide_info") : t("auth.show_info")}
              {showCompany ? (
                <UpIcon style={{ marginLeft: 8 }} />
              ) : (
                <DownIcon style={{ marginLeft: 8 }} />
              )}
            </Button>
          </div>
        ) : (
          ""
        )}
        {foundByBusinessId && showCompany ? (
          <Grid
            container
            spacing={1}
            justifyContent="flex-start"
            style={{ marginTop: 4 }}
          >
            {/* Basic Company Info */}
            {[
              { label: t("auth.business_id"), value: companyData.businessId },
              { label: t("auth.company_name"), value: companyData.name },
              { label: t("auth.address"), value: companyData.address },
              { label: t("contact.phone"), value: companyData.phone },
              {
                label: t("auth.business_form"),
                value: companyData.businessForm,
              },
              {
                label: t("auth.business_line"),
                value: companyData.businessline,
              },
            ]
              .filter((item) => item.value)
              .map((item, index) => (
                <Grid item xs={12} key={`basic-${index}`}>
                  <Typography variant="body2" align="left">
                    <strong>{item.label}:</strong> {item.value}
                  </Typography>
                </Grid>
              ))}

            {/* Registry Information */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                align="left"
                style={{ marginTop: 16, marginBottom: 8, fontWeight: "bold" }}
              >
                {t("auth.registry_information")}:
              </Typography>
            </Grid>

            {/* Trade Register */}
            {companyData.hasTradeRegister && (
              <Grid item xs={12}>
                <Typography variant="body2" align="left">
                  <strong>{t("registrations.trade_register")}:</strong>{" "}
                  {companyData.tradeRegisterStatus === "Rekisterissä"
                    ? t("registrations.statuses.registered")
                    : companyData.tradeRegisterStatus === "Lakannut"
                      ? t("registrations.statuses.ceased")
                      : companyData.tradeRegisterStatus ===
                        "Poistettu rekisteristä"
                        ? t("registrations.statuses.removed_from_register")
                        : companyData.tradeRegisterStatus ===
                          "Ei rekisteröity perustaminen"
                          ? t("registrations.statuses.not_registered_establishment")
                          : companyData.tradeRegisterStatus ||
                          t("registrations.statuses.unregistered")}
                </Typography>
              </Grid>
            )}

            {/* Tax Authority */}
            {companyData.hasTaxAuthority && (
              <Grid item xs={12}>
                <Typography variant="body2" align="left">
                  <strong>{t("registrations.tax_authority")}:</strong>{" "}
                  {companyData.taxAuthorityStatus === "Rekisterissä"
                    ? t("registrations.statuses.registered")
                    : companyData.taxAuthorityStatus ||
                    t("registrations.statuses.unregistered")}
                </Typography>
              </Grid>
            )}

            {/* VAT Registration */}
            {companyData.hasVatRegistration && (
              <Grid item xs={12}>
                <Typography variant="body2" align="left">
                  <strong>{t("registrations.vat_liability")}:</strong>{" "}
                  {companyData.vatStatus ===
                    "Liiketoiminnasta arvonlisäverovelvollinen"
                    ? t(
                      "registrations.statuses.vat_statuses.business_vat_liable"
                    )
                    : companyData.vatStatus === "Rekisterissä"
                      ? t("registrations.statuses.registered")
                      : companyData.vatStatus ||
                      t("registrations.statuses.unregistered")}
                </Typography>
              </Grid>
            )}

            {/* Prepayment Register */}
            {companyData.hasPrepaymentRegister && (
              <Grid item xs={12}>
                <Typography variant="body2" align="left">
                  <strong>{t("registrations.prepayment_register")}:</strong>{" "}
                  {companyData.prepaymentRegisterStatus === "Rekisterissä"
                    ? t("registrations.statuses.registered")
                    : companyData.prepaymentRegisterStatus ||
                    t("registrations.statuses.unregistered")}
                </Typography>
              </Grid>
            )}

            {/* Employer Register */}
            {companyData.hasEmployerRegister && (
              <Grid item xs={12}>
                <Typography variant="body2" align="left">
                  <strong>{t("registrations.employer_register")}:</strong>{" "}
                  {companyData.employerRegisterStatus === "Rekisterissä"
                    ? t("registrations.statuses.registered")
                    : companyData.employerRegisterStatus ||
                    t("registrations.statuses.unregistered")}
                </Typography>
              </Grid>
            )}

            {/* Foundation Register */}
            {companyData.hasFoundationRegister && (
              <Grid item xs={12}>
                <Typography variant="body2" align="left">
                  <strong>{t("registrations.foundation_register")}:</strong>{" "}
                  {companyData.foundationRegisterStatus === "Rekisterissä"
                    ? t("registrations.statuses.registered")
                    : companyData.foundationRegisterStatus ||
                    t("registrations.statuses.unregistered")}
                </Typography>
              </Grid>
            )}

            {/* Association Register */}
            {companyData.hasAssociationRegister && (
              <Grid item xs={12}>
                <Typography variant="body2" align="left">
                  <strong>{t("registrations.association_register")}:</strong>{" "}
                  {companyData.associationRegisterStatus === "Rekisterissä"
                    ? t("registrations.statuses.registered")
                    : companyData.associationRegisterStatus ||
                    t("registrations.statuses.unregistered")}
                </Typography>
              </Grid>
            )}

            {/* Insurance Premium Tax Register */}
            {companyData.hasInsurancePremiumTax && (
              <Grid item xs={12}>
                <Typography variant="body2" align="left">
                  <strong>
                    {t("registrations.insurance_premium_tax_register")}:
                  </strong>{" "}
                  {companyData.insurancePremiumTaxStatus === "Rekisterissä"
                    ? t("registrations.statuses.registered")
                    : companyData.insurancePremiumTaxStatus ||
                    t("registrations.statuses.unregistered")}
                </Typography>
              </Grid>
            )}
          </Grid>
        ) : (
          ""
        )}
      </div>

      <hr className={classes.hr} />

      <TextInput
        value={data.password.value}
        onChange={props.onChangeData("password")}
        required={data.password.required}
        error={data.password.error}
        label={t("auth.password")}
        type="password"
        fullWidth={true}
        helpertext={passwordHelper(data.password.value, data.password.value)}
        onKeyPress={handleKeyPress}
        margin="dense"
      />

      <Typography variant="caption">
        {t("auth.password_requirements")}
      </Typography>

      <TextInput
        value={data.password2.value}
        onChange={props.onChangeData("password2")}
        required={data.password2.required}
        error={!props.isValid(data.password2)}
        label={t("auth.password2")}
        type="password"
        fullWidth={true}
        helpertext={password2Helper(data.password.value, data.password2.value)}
        onKeyPress={handleKeyPress}
        margin="dense"
      />

      <div className={classes.termsContainer}>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={() => setTermsAccepted(!termsAccepted)}
                name="termsAccepted"
                color="primary"
              />
            }
            label={t("info.i_accept_terms")}
          />
        </FormGroup>
        {/* <ButtonBase
          className={classes.termsButton}
          onClick={() => setTermsDialogOpen(true)}
        >
          {t('info.terms_of_service')} <OpenInNewIcon className={classes.termsButtonIcon} />
        </ButtonBase> */}
        <ButtonBase
          className={classes.termsButton}
          onClick={() => setPrivacyDialogOpen(true)}
        >
          {t("info.privacy_policy")}{" "}
          <OpenInNewIcon className={classes.termsButtonIcon} />
        </ButtonBase>
      </div>

      <Button
        className={classes.button}
        disabled={!submitEnabled || !termsAccepted}
        fullWidth
        onClick={register}
        variant="contained"
        color="primary"
      >
        {t("auth.register")}
        {loading && <Loading className={classes.loading} />}
      </Button>

      <TermsDialog
        type={"privacy"}
        open={privacyDialogOpen}
        onClose={() => setPrivacyDialogOpen(false)}
      />
    </form>
  );
}

RegistrationForm.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  email: PropTypes.string.isRequired,
  error: PropTypes.string,
  isPhoneAccount: PropTypes.bool.isRequired,
  isValid: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onBlurTaxNumber: PropTypes.func.isRequired,
  onChangeBusinessId: PropTypes.func.isRequired,
  onChangeData: PropTypes.func.isRequired,
  passwordHelper: PropTypes.func.isRequired,
  password2Helper: PropTypes.func.isRequired,
  phone: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  submitEnabled: PropTypes.bool.isRequired,
  togglePhoneAccount: PropTypes.func.isRequired,
  businessIdCountry: PropTypes.string.isRequired,
  handleBusinessIdCountryRadioChange: PropTypes.func.isRequired,
};

export default withStyles(styles)(RegistrationForm);
